<script setup lang="ts">
// Select avec recherche — 100% DaisyUI/Tailwind (pas de dépendance externe),
// pour rester cohérent avec le design system V2.
import { computed, nextTick, ref, watch } from 'vue'
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/vue/24/outline'

export interface SelectOption {
  value: string
  label: string
  disabled?: boolean
}

const props = withDefaults(
  defineProps<{
    modelValue: string
    options: SelectOption[]
    placeholder?: string
    searchPlaceholder?: string
    disabled?: boolean
    id?: string
    clearable?: boolean
  }>(),
  {
    placeholder: '— Sélectionner —',
    searchPlaceholder: 'Rechercher…',
    disabled: false,
    clearable: false,
  },
)

const emit = defineEmits<{ 'update:modelValue': [string] }>()

const open = ref(false)
const query = ref('')
const activeIndex = ref(-1)
const root = ref<HTMLElement | null>(null)
const searchInput = ref<HTMLInputElement | null>(null)

const selectedLabel = computed(() => props.options.find((o) => o.value === props.modelValue)?.label ?? '')

const filtered = computed(() => {
  const q = query.value.trim().toLowerCase()
  if (!q) return props.options
  return props.options.filter((o) => o.label.toLowerCase().includes(q))
})

function openMenu() {
  if (props.disabled) return
  open.value = true
  query.value = ''
  activeIndex.value = filtered.value.findIndex((o) => !o.disabled)
  nextTick(() => searchInput.value?.focus())
}

function closeMenu() {
  open.value = false
}

function toggle() {
  if (open.value) closeMenu()
  else openMenu()
}

function select(option: SelectOption) {
  if (option.disabled) return
  emit('update:modelValue', option.value)
  closeMenu()
}

function clear(e: Event) {
  e.stopPropagation()
  emit('update:modelValue', '')
}

function onKeydown(e: KeyboardEvent) {
  if (!open.value && (e.key === 'ArrowDown' || e.key === 'Enter')) {
    e.preventDefault()
    openMenu()
    return
  }
  if (!open.value) return

  if (e.key === 'Escape') {
    e.preventDefault()
    closeMenu()
  } else if (e.key === 'ArrowDown') {
    e.preventDefault()
    moveActive(1)
  } else if (e.key === 'ArrowUp') {
    e.preventDefault()
    moveActive(-1)
  } else if (e.key === 'Enter') {
    e.preventDefault()
    const opt = filtered.value[activeIndex.value]
    if (opt) select(opt)
  }
}

function moveActive(delta: number) {
  const list = filtered.value
  if (list.length === 0) return
  let i = activeIndex.value
  for (let step = 0; step < list.length; step++) {
    i = (i + delta + list.length) % list.length
    if (!list[i].disabled) {
      activeIndex.value = i
      return
    }
  }
}

// Reset de l'index actif quand la liste filtrée change.
watch(filtered, () => {
  activeIndex.value = filtered.value.findIndex((o) => !o.disabled)
})

function onClickOutside(e: MouseEvent) {
  if (root.value && !root.value.contains(e.target as Node)) closeMenu()
}

watch(open, (v) => {
  if (v) document.addEventListener('mousedown', onClickOutside)
  else document.removeEventListener('mousedown', onClickOutside)
})
</script>

<template>
  <div ref="root" class="relative">
    <button
      :id="id"
      type="button"
      class="select select-bordered flex w-full items-center justify-between gap-2 text-left font-normal"
      :class="{ 'select-disabled': disabled }"
      :disabled="disabled"
      role="combobox"
      :aria-expanded="open"
      aria-haspopup="listbox"
      @click="toggle"
      @keydown="onKeydown"
    >
      <span class="truncate" :class="{ 'text-base-content/50': !selectedLabel }">
        {{ selectedLabel || placeholder }}
      </span>
      <span
        v-if="clearable && modelValue && !disabled"
        class="btn btn-ghost btn-xs btn-circle"
        role="button"
        aria-label="Effacer"
        @click="clear"
      >
        <XMarkIcon class="h-4 w-4" aria-hidden="true" />
      </span>
    </button>

    <div
      v-if="open"
      class="absolute z-20 mt-1 w-full rounded-box border border-base-300 bg-base-100 shadow-lg"
    >
      <div class="border-b border-base-200 p-2">
        <label class="input input-sm input-bordered flex items-center gap-2">
          <MagnifyingGlassIcon class="h-4 w-4 shrink-0 text-base-content/60" aria-hidden="true" />
          <input
            ref="searchInput"
            v-model="query"
            type="text"
            class="grow"
            :placeholder="searchPlaceholder"
            @keydown="onKeydown"
          />
        </label>
      </div>

      <ul role="listbox" class="menu max-h-60 flex-nowrap overflow-y-auto p-2">
        <li v-if="filtered.length === 0" class="px-2 py-1.5 text-sm text-base-content/60">Aucun résultat</li>
        <li v-for="(option, i) in filtered" :key="option.value">
          <a
            role="option"
            :aria-selected="option.value === modelValue"
            :class="{
              active: option.value === modelValue,
              'bg-base-200': i === activeIndex && option.value !== modelValue,
              'pointer-events-none opacity-40': option.disabled,
            }"
            @click="select(option)"
            @mousemove="activeIndex = i"
          >
            <span class="truncate">{{ option.label }}</span>
          </a>
        </li>
      </ul>
    </div>
  </div>
</template>
